import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import * as ContentActions from '../../../../../actions/content'
import { ACL, colors } from '../../../../../constants'
import { Confirmation, Headings } from '../../../../../components'
import Button, { Sizes as ButtonSizes } from '../../../../../components/Button'
import Padding, { Sizes as PaddingSizes } from '../../../../../components/Padding'
import * as MediaSelectors from '../../../../../reducers/content/media/selectors'
import Media, { MediaApprovalStatusValues } from '../../../../../types/Media'
import { EstateUtils } from '../../../../../utils'

export default function MediaApproval(): React.ReactElement {
    const selectedMedia: Media = useSelector(MediaSelectors.selectedMedia)
    const [description, setDescription] = React.useState('')

    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { approval_description: approvalDescription, id: mediaId, is_approved: status }: Media = selectedMedia || {}

    const canApproveMedia: boolean = useSelector((state) => EstateUtils.hasPermission(state, { acls: [ACL.APPROVE_MEDIA] }))

    const onApproveMediaClick = (): void => {
        dispatch(ContentActions.approveMedia(mediaId, true))
    }

    const onRejectMediaClick = (): void => {
        dispatch(ContentActions.approveMedia(mediaId, false, { description }))
    }

    const onChangeDescription = (event: React.FormEvent) => {
        const target = event.target as HTMLTextAreaElement
        const value = target.value as string
        setDescription(value)
    }

    switch (status) {
        /**
         * Pending
         */
        case MediaApprovalStatusValues.Pending: {
            return (
                <Padding top size={PaddingSizes.md}>
                    <Padding bottom size={PaddingSizes.xs}>
                        <FontAwesomeIcon icon="exclamation-circle" />
                        &nbsp;
                        <Headings.H3 bold>{t('media-approval-pending')}</Headings.H3>
                    </Padding>
                    {canApproveMedia && (
                        <>
                            <Padding autoWidth inline right size={PaddingSizes.sm}>
                                <Confirmation
                                    confirmation={t('media-approval-approve-confirmation')}
                                    header={t('media-approval-approve-confirmation', {
                                        context: 'title',
                                    })}
                                    ok={t('media-approval-approve')}
                                    cancel={t('cancel')}
                                    onCancel={() => null}
                                    onConfirm={onApproveMediaClick}
                                >
                                    <Button size={ButtonSizes.md}>{t('media-approval-approve')}</Button>
                                </Confirmation>
                            </Padding>
                            <Confirmation
                                confirmation={t('media-approval-reject-confirmation')}
                                header={t('media-approval-reject-confirmation', {
                                    context: 'title',
                                })}
                                ok={t('media-approval-reject')}
                                cancel={t('cancel')}
                                onCancel={() => setDescription('')}
                                onChangeText={onChangeDescription}
                                onConfirm={onRejectMediaClick}
                                text={description}
                                withTextArea
                            >
                                <Button size={ButtonSizes.md} theme="danger">
                                    {t('media-approval-reject')}
                                </Button>
                            </Confirmation>
                        </>
                    )}
                </Padding>
            )
        }
        /**
         * Rejected
         */
        case MediaApprovalStatusValues.Rejected: {
            return (
                <Padding top bottom size={PaddingSizes.sm}>
                    <Padding bottom size={PaddingSizes.sm}>
                        <Headings.H3 color={colors.reddark}>{t('media-approval-rejected')}</Headings.H3>
                    </Padding>
                    <Headings.H3 italic>{approvalDescription}</Headings.H3>
                </Padding>
            )
        }
        /**
         * Tested OK
         */
        default: {
            return (
                <Padding top size={PaddingSizes.sm}>
                    <Headings.H3 color={colors.grey}>
                        <FontAwesomeIcon icon="check" />
                        &nbsp;
                        {t('media-approval-approved')}
                    </Headings.H3>
                </Padding>
            )
        }
    }
}
